import { useState } from "react";
import { VITE_SUPABASE_URL } from "@/utils/const";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, Calendar, Users } from "lucide-react";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface AvailabilitySettingsStepProps {
	onNext: () => void;
	onBack: () => void;
	initialData?: {
		bookingWindowMonths?: number;
		availabilityGuardrails?: AvailabilityGuardrails;
	};
}

interface DaySettings {
	available: boolean;
	startTime: string;
	endTime: string;
}

interface AvailabilityGuardrails {
	monday: DaySettings;
	tuesday: DaySettings;
	wednesday: DaySettings;
	thursday: DaySettings;
	friday: DaySettings;
	saturday: DaySettings;
	sunday: DaySettings;
}

const DAYS = [
	{ key: "monday", label: "Monday" },
	{ key: "tuesday", label: "Tuesday" },
	{ key: "wednesday", label: "Wednesday" },
	{ key: "thursday", label: "Thursday" },
	{ key: "friday", label: "Friday" },
	{ key: "saturday", label: "Saturday" },
	{ key: "sunday", label: "Sunday" },
];

export const AvailabilitySettingsStep = ({
	onNext,
	onBack,
	initialData,
}: AvailabilitySettingsStepProps) => {
	const [availabilityGuardrails, setAvailabilityGuardrails] =
		useState<AvailabilityGuardrails>(
			initialData?.availabilityGuardrails || {
				monday: { available: false, startTime: "09:00", endTime: "17:00" },
				tuesday: { available: false, startTime: "09:00", endTime: "17:00" },
				wednesday: { available: false, startTime: "09:00", endTime: "17:00" },
				thursday: { available: false, startTime: "09:00", endTime: "17:00" },
				friday: { available: false, startTime: "09:00", endTime: "17:00" },
				saturday: { available: false, startTime: "09:00", endTime: "17:00" },
				sunday: { available: false, startTime: "09:00", endTime: "17:00" },
			},
		);

	const [bookingWindowMonths, setBookingWindowMonths] = useState<number>(
		initialData?.bookingWindowMonths || 6,
	);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleDayToggle = (day: string) => {
		setAvailabilityGuardrails((prev) => ({
			...prev,
			[day]: {
				...prev[day as keyof AvailabilityGuardrails],
				available: !prev[day as keyof AvailabilityGuardrails].available,
			},
		}));
	};

	const handleTimeChange = (
		day: string,
		field: "startTime" | "endTime",
		value: string,
	) => {
		setAvailabilityGuardrails((prev) => ({
			...prev,
			[day]: {
				...prev[day as keyof AvailabilityGuardrails],
				[field]: value,
			},
		}));
	};

	const handleSubmit = async () => {
		setLoading(true);
		setError(null);

		try {
			const hasAvailableDays = Object.values(availabilityGuardrails).some(
				(day) => day.available,
			);
			if (!hasAvailableDays) {
				setError("Please select at least one day for availability.");
				return;
			}

			if (bookingWindowMonths < 1 || bookingWindowMonths > 10) {
				setError("Booking window must be between 1 and 10 months.");
				return;
			}

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "availability_settings",
						data: {
							availability_settings: {
								guardrails: availabilityGuardrails,
								booking_window_months: bookingWindowMonths,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to save availability settings");
			}

			onNext();
		} catch (error) {
			console.error("Failed to save availability settings:", error);
			setError("Failed to save settings. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className={styles.wrapperNarrow}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "90%" }} />
				</div>
				<p className={styles.progressNote}>Step 13 of 15</p>
			</div>

			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Clock className={styles.cardIcon} />
						Availability Settings
					</CardTitle>
					<CardDescription className={styles.cardSubtitle}>
						Configure when you're available for intro calls and how far in
						advance you accept birth bookings.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{error && (
						<Alert variant="destructive">
							<AlertDescription className="font-karla">{error}</AlertDescription>
						</Alert>
					)}

					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-newTheme-black flex items-center gap-2 font-karla">
							<Users className="h-5 w-5 text-newTheme-spruce" />
							Booking Window
						</h3>
						<p className="text-sm text-newTheme-black/60 font-karla">
							How many months in advance do you accept birth bookings? This
							helps families know if you're available for their due date.
						</p>

						<div className={styles.innerPanel}>
							<Label htmlFor="booking-window" className={styles.fieldLabel}>
								Maximum months in advance
							</Label>
							<div className="flex items-center gap-3 mt-2">
								<Input
									id="booking-window"
									type="number"
									min="1"
									max="10"
									value={bookingWindowMonths}
									onChange={(e) =>
										setBookingWindowMonths(parseInt(e.target.value) || 1)
									}
									className="w-24 font-karla"
								/>
								<span className="text-sm text-newTheme-black/60 font-karla">months</span>
							</div>
							<p className={styles.helperText}>
								Example: If set to 6 months, families due within 6 months can
								book with you.
							</p>
						</div>
					</div>

					<div className="space-y-4">
						<h3 className="text-lg font-semibold text-newTheme-black flex items-center gap-2 font-karla">
							<Calendar className="h-5 w-5 text-newTheme-spruce" />
							Weekly Schedule for Intro Calls
						</h3>
						<p className="text-sm text-newTheme-black/60 font-karla">
							Select the days and times you're available for intro calls with
							families.
						</p>

						<div className="space-y-3">
							{DAYS.map(({ key, label }) => {
								const daySettings =
									availabilityGuardrails[key as keyof AvailabilityGuardrails];
								return (
									<div key={key} className={styles.innerPanel}>
										<div className="flex items-center justify-between">
											<Label
												htmlFor={`${key}-toggle`}
												className={styles.fieldLabel}
											>
												{label}
											</Label>
											<Switch
												id={`${key}-toggle`}
												checked={daySettings.available}
												onCheckedChange={() => handleDayToggle(key)}
											/>
										</div>

										{daySettings.available && (
											<div className="flex items-center gap-3">
												<div className="flex-1">
													<Label
														htmlFor={`${key}-start`}
														className="text-xs text-newTheme-black/50 font-karla"
													>
														Start Time
													</Label>
													<Input
														id={`${key}-start`}
														type="time"
														value={daySettings.startTime}
														onChange={(e) =>
															handleTimeChange(key, "startTime", e.target.value)
														}
														className="mt-1 font-karla"
													/>
												</div>
												<div className="text-newTheme-black/40 font-karla">to</div>
												<div className="flex-1">
													<Label
														htmlFor={`${key}-end`}
														className="text-xs text-newTheme-black/50 font-karla"
													>
														End Time
													</Label>
													<Input
														id={`${key}-end`}
														type="time"
														value={daySettings.endTime}
														onChange={(e) =>
															handleTimeChange(key, "endTime", e.target.value)
														}
														className="mt-1 font-karla"
													/>
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>

					<div className={styles.infoAlert}>
						<p className={styles.infoText}>
							<strong>How this works:</strong> Families will only see available
							slots within your specified days and times. The booking window
							ensures families know if you're available for their due date
							before contacting you.
						</p>
					</div>
				</CardContent>
			</Card>

			<div className={`${styles.navRow} mt-8`}>
				<Button
					variant="outline"
					onClick={onBack}
					className={styles.backButton}
				>
					Back
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={loading}
					className={styles.submitButton}
				>
					{loading ? (
						<div className="flex items-center">
							<div className={styles.spinner} />
							Saving...
						</div>
					) : (
						"Continue"
					)}
				</Button>
			</div>
		</motion.div>
	);
};
